/**
 * HelpPage — the in-app user guide. Lists the guide sections (from the backend
 * /api/v1/help endpoints, which serve docs/USER_GUIDE) in a grouped table of
 * contents and renders the selected section's Markdown with HelpMarkdown.
 *
 * Route: /help and /help/:slug (inside MainLayout).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { helpApi, HelpSection } from './helpApi';
import { HelpMarkdown } from './HelpMarkdown';
import { MONO_FONT } from '@/shared/typography';

interface GroupBlock {
  group: string;
  minOrder: string;
  sections: HelpSection[];
}

function groupSections(sections: HelpSection[]): GroupBlock[] {
  const byGroup = new Map<string, HelpSection[]>();
  for (const s of sections) {
    const arr = byGroup.get(s.group) ?? [];
    arr.push(s);
    byGroup.set(s.group, arr);
  }
  const blocks: GroupBlock[] = [];
  byGroup.forEach((secs, group) => {
    secs.sort((a, b) => a.order.localeCompare(b.order));
    blocks.push({ group, minOrder: secs[0]?.order ?? '99', sections: secs });
  });
  blocks.sort((a, b) => a.minOrder.localeCompare(b.minOrder));
  return blocks;
}

export const HelpPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();

  const [sections, setSections] = useState<HelpSection[]>([]);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const [loadingSections, setLoadingSections] = useState(true);
  const [filter, setFilter] = useState('');

  const [markdown, setMarkdown] = useState<string>('');
  const [contentTitle, setContentTitle] = useState<string>('');
  const [contentError, setContentError] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);

  // Load the table of contents once.
  useEffect(() => {
    let alive = true;
    setLoadingSections(true);
    helpApi
      .listSections()
      .then((res) => {
        if (!alive) return;
        setSections(res.sections ?? []);
        setSectionsError(null);
      })
      .catch(() => {
        if (!alive) return;
        setSectionsError('The user guide could not be loaded.');
      })
      .finally(() => {
        if (alive) setLoadingSections(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const activeSlug = slug ?? sections[0]?.slug;

  // If we landed on /help with no slug, redirect to the first section.
  useEffect(() => {
    if (!slug && sections.length > 0) {
      navigate(`/help/${sections[0].slug}`, { replace: true });
    }
  }, [slug, sections, navigate]);

  // Load the active section's Markdown.
  useEffect(() => {
    if (!activeSlug) return;
    let alive = true;
    setLoadingContent(true);
    setContentError(null);
    helpApi
      .getSection(activeSlug)
      .then((res) => {
        if (!alive) return;
        setMarkdown(res.markdown ?? '');
        setContentTitle(res.title ?? '');
      })
      .catch(() => {
        if (!alive) return;
        setContentError('This help section could not be loaded.');
        setMarkdown('');
      })
      .finally(() => {
        if (alive) setLoadingContent(false);
      });
    return () => {
      alive = false;
    };
  }, [activeSlug]);

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const filtered = q
      ? sections.filter((s) => s.title.toLowerCase().includes(q) || s.slug.includes(q))
      : sections;
    return groupSections(filtered);
  }, [sections, filter]);

  // ── Table of contents (left) ──
  const toc = (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 120px)',
        position: 'sticky',
        top: 0,
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <MenuBookIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>User Guide</Typography>
        </Box>
        <TextField
          size="small"
          fullWidth
          placeholder="Filter sections…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
        />
      </Box>
      <Box sx={{ overflowY: 'auto', flex: 1, py: 0.5 }}>
        {groups.map((block) => (
          <Box key={block.group} sx={{ mb: 0.5 }}>
            <Typography
              sx={{
                px: 1.75,
                pt: 1.25,
                pb: 0.5,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontFamily: MONO_FONT,
                color: theme.palette.text.disabled,
              }}
            >
              {block.group}
            </Typography>
            <List dense disablePadding>
              {block.sections.map((s) => (
                <ListItemButton
                  key={s.slug}
                  selected={s.slug === activeSlug}
                  onClick={() => navigate(`/help/${s.slug}`)}
                  sx={{ py: 0.4, px: 1.75, borderRadius: 0 }}
                >
                  <ListItemText
                    primary={s.title}
                    primaryTypographyProps={{
                      fontSize: 12.5,
                      fontWeight: s.slug === activeSlug ? 600 : 400,
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
        {groups.length === 0 && !loadingSections && (
          <Typography sx={{ px: 1.75, py: 1, fontSize: 12, color: theme.palette.text.disabled }}>
            No sections match.
          </Typography>
        )}
      </Box>
    </Box>
  );

  // ── Content (right) ──
  let content: React.ReactNode;
  if (loadingSections) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  } else if (sectionsError) {
    content = <Alert severity="error">{sectionsError}</Alert>;
  } else if (sections.length === 0) {
    content = <Alert severity="info">The user guide has no sections yet.</Alert>;
  } else if (loadingContent) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  } else if (contentError) {
    content = <Alert severity="error">{contentError}</Alert>;
  } else {
    content = <HelpMarkdown markdown={markdown} />;
  }

  return (
    <Box sx={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
      {toc}
      <Box sx={{ flex: 1, minWidth: 0, px: { xs: 0, md: 4 }, py: 1, maxWidth: 920 }}>
        {/* Mobile section picker */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Section"
            value={activeSlug ?? ''}
            onChange={(e) => navigate(`/help/${e.target.value}`)}
            SelectProps={{ native: true }}
          >
            {sections.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.title}
              </option>
            ))}
          </TextField>
        </Box>
        {content}
      </Box>
    </Box>
  );
};

export default HelpPage;
